/**
 * Button component for canvas rendering.
 * Triggers actions with optional confirmation dialog.
 *
 * Task: T051 (Button component), T061 (confirmation dialog)
 */

import React, { memo, useState, useCallback } from 'react';
import { Loader2, AlertTriangle, X } from 'lucide-react';
import type { ButtonProps as ButtonPropsType, ButtonVariant } from '../../../types/canvas';

export interface ButtonComponentProps extends ButtonPropsType {
  onAction?: (action: { name: string; params?: Record<string, unknown> }) => Promise<void>;
  error?: string;
}

// ============================================================================
// Confirmation Dialog (T061)
// ============================================================================

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
  variant?: ButtonVariant;
}

function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmLabel,
  onConfirm,
  onCancel,
  loading,
  variant,
}: ConfirmDialogProps) {
  if (!isOpen) return null;

  const confirmButtonClasses = {
    primary: 'bg-blue-600 hover:bg-blue-700 text-white',
    secondary: 'bg-gray-600 hover:bg-gray-700 text-white',
    danger: 'bg-red-600 hover:bg-red-700 text-white',
    ghost: 'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            {variant === 'danger' && <AlertTriangle size={18} className="text-red-500" />}
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            aria-label="Close confirmation dialog"
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="px-4 py-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">{message}</p>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`px-4 py-2 text-sm font-medium rounded-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
              confirmButtonClasses[variant || 'primary']
            }`}
          >
            {loading && <Loader2 size={14} className="animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Button Component
// ============================================================================

function ButtonComponentBase({
  label,
  action,
  variant = 'primary',
  disabled = false,
  loading = false,
  onAction,
  error,
}: ButtonComponentProps) {
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const variantClasses: Record<ButtonVariant, string> = {
    primary: 'bg-blue-600 hover:bg-blue-700 text-white',
    secondary: 'bg-gray-600 hover:bg-gray-700 text-white',
    danger: 'bg-red-600 hover:bg-red-700 text-white',
    ghost: 'bg-transparent hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600',
  };

  const handleClick = useCallback(async () => {
    if (!onAction || disabled || loading || isLoading) return;

    // T061: Check for confirmation requirement
    if (action.confirm) {
      setShowConfirm(true);
      return;
    }

    // Execute action directly
    setIsLoading(true);
    try {
      await onAction({
        name: action.name,
        params: action.params,
      });
    } finally {
      setIsLoading(false);
    }
  }, [onAction, action, disabled, loading, isLoading]);

  const handleConfirm = useCallback(async () => {
    if (!onAction) return;

    setIsLoading(true);
    try {
      await onAction({
        name: action.name,
        params: action.params,
      });
      setShowConfirm(false);
    } finally {
      setIsLoading(false);
    }
  }, [onAction, action]);

  const handleCancel = useCallback(() => {
    setShowConfirm(false);
  }, []);

  const isDisabled = disabled || loading || isLoading;
  const showLoader = loading || isLoading;

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={isDisabled}
        className={`inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${variantClasses[variant]}`}
      >
        {showLoader && <Loader2 size={14} className="animate-spin" />}
        {label}
      </button>

      {error && (
        <p className="mt-1 text-xs text-red-600">{error}</p>
      )}

      {/* T061: Confirmation Dialog */}
      {action.confirm && (
        <ConfirmDialog
          isOpen={showConfirm}
          title={action.confirm.title}
          message={action.confirm.message}
          confirmLabel={label}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
          loading={isLoading}
          variant={variant}
        />
      )}
    </>
  );
}

export const ButtonComponent = memo(ButtonComponentBase);
